import { FormattedMessage, useIntl } from 'react-intl';
import type { Key } from 'react';
import * as React from 'react';
import cx from 'clsx';
import {
  Button as AriaButton,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
} from 'react-aria-components';
import {
  BadgeStatus,
  Button,
  Disclaimer,
  ExternalLink,
  type StatusProps,
} from '@repo/design-system-kit';
import { IconChevronBottomOutlined, IconDownloadOutlined } from '@repo/monochrome-icons';
import styles from './checkout-header.module.css';

const BADGE_LEVELS: Record<CheckoutHeaderProps['status'], StatusProps['level']> = {
  open: 'in-progress',
  paid: 'validated',
};
export interface CheckoutHeaderProps {
  amount: string;
  organizationName: string;
  status: 'open' | 'paid';
  invoiceNumber?: string;
  locales: {
    id: string;
    label: string;
  }[];
  selectedLocale?: string;
  pdfUrl?: string;
  onLocaleSelectionChange?: (key: Key) => void;
  shouldShowDisclaimer?: boolean;
  onDownloadPress?: () => void;
  downloadLabel?: string;
  hideDownloadButton?: boolean;
  isClientPortalEnabled?: boolean;
  clientId?: string;
  shouldDisplayActions?: boolean;
}

export function CheckoutHeader({
  amount,
  organizationName,
  status,
  invoiceNumber,
  locales,
  selectedLocale,
  onLocaleSelectionChange,
  pdfUrl,
  shouldShowDisclaimer = false,
  onDownloadPress,
  downloadLabel,
  hideDownloadButton = false,
  isClientPortalEnabled = false,
  clientId,
  shouldDisplayActions = false,
}: CheckoutHeaderProps): React.JSX.Element {
  const { formatMessage } = useIntl();

  const badgeTexts = {
    open: formatMessage(
      { id: 'receivable-invoices.public-payment-link.status.unpaid' },
      { language: selectedLocale }
    ),
    paid: formatMessage(
      { id: 'receivable-invoices.public-payment-link.status.paid' },
      { language: selectedLocale }
    ),
  };

  const downloadButtonContent = (
    <>
      <IconDownloadOutlined aria-hidden="true" className="mr-8" />
      {downloadLabel ||
        formatMessage(
          { id: 'receivable-invoices.public-payment-link.download' },
          { language: selectedLocale }
        )}
    </>
  );

  return (
    <header className={styles.header}>
      {shouldShowDisclaimer ? (
        <Disclaimer.Inline data-testid="preview-disclaimer" level="info">
          <FormattedMessage
            id="receivable-invoices.payment-link-creation.preview.disclaimer"
            values={{
              language: selectedLocale,
            }}
          />
        </Disclaimer.Inline>
      ) : null}
      {shouldDisplayActions ? (
        <div className={styles.actions} data-testid="actions">
          <Select
            className={styles['locale-switcher']}
            onSelectionChange={onLocaleSelectionChange}
            selectedKey={selectedLocale}
          >
            <AriaButton className={cx('body-1', styles.trigger)} data-testid="trigger">
              <SelectValue />
              <IconChevronBottomOutlined aria-hidden="true" />
            </AriaButton>
            <Popover
              UNSTABLE_portalContainer={document.getElementById('ember-testing') ?? document.body}
              className={styles.popover}
              maxHeight={200}
              placement="bottom right"
            >
              <ListBox items={locales}>
                {item => (
                  <ListBoxItem
                    className={cx('body-1', styles['list-item'])}
                    data-testid={item.id}
                    key={item.id}
                  >
                    {item.label}
                  </ListBoxItem>
                )}
              </ListBox>
            </Popover>
          </Select>
          {isClientPortalEnabled ? (
            <a className={styles.btn} data-testid="client-portal-cta" href={`/clients/${clientId}`}>
              <FormattedMessage id="invoice.preview.cta.portal" />
            </a>
          ) : null}
        </div>
      ) : null}

      <div className={styles.content}>
        <BadgeStatus level={BADGE_LEVELS[status]} text={badgeTexts[status]} />
        {Boolean(invoiceNumber) && (
          <div className="title-3" data-checkout-header-title>
            <FormattedMessage
              id="receivable-invoices.public-payment-link.invoice-number"
              values={{
                invoiceNumber,
                language: selectedLocale,
              }}
            />
          </div>
        )}

        {!hideDownloadButton && (
          <div className={styles.actions}>
            {onDownloadPress ? (
              <Button
                data-testid="download"
                onPress={onDownloadPress}
                size="small"
                variant="tertiary"
              >
                {downloadButtonContent}
              </Button>
            ) : (
              <ExternalLink
                data-testid="download"
                display="button"
                href={pdfUrl}
                size="small"
                variant="tertiary"
              >
                {downloadButtonContent}
              </ExternalLink>
            )}
          </div>
        )}
        <div>
          <h1 className={cx(styles.title, 'title-1')}>
            <span className="mr-8" data-checkout-header-amount>
              {amount}
            </span>
            <span className={cx(styles.vat, 'caption')}>
              <FormattedMessage
                id="receivable-invoices.public-payment-link.include-vat"
                values={{ language: selectedLocale }}
              />
            </span>
          </h1>
          <div className="title-4" data-checkout-header-org-name>
            <FormattedMessage
              id="receivable-invoices.public-payment-link.sent-by"
              values={{ companyName: organizationName, language: selectedLocale }}
            />
          </div>
        </div>
      </div>
    </header>
  );
}
